import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { usePageHeader } from '../context/PageHeaderContext';

// AI Character configurations
const AI_CHARACTERS = {
  orb: {
    id: 'orb',
    name: 'ORB Ai Assistant',
    shortDesc: 'Friendly, clear, and action-oriented',
    image: null,
    color: '#586D52'
  },
  mena: {
    id: 'mena',
    name: 'Mena ORB',
    shortDesc: 'Strategic, ethical, and future‑focused',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/67lqvc7a_Mena%20Standard.png',
    color: '#8B5CF6'
  },
  katie: {
    id: 'katie',
    name: 'Katie ORB',
    shortDesc: 'Warm, steady, and deeply supportive',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/b90ayuat_katie%20standard.png',
    color: '#EC4899'
  },
  luke: {
    id: 'luke',
    name: 'Luke ORB',
    shortDesc: 'Quiet, precise, and trust‑driven',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/ue5pv5dv_Luke%20Standard.png',
    color: '#3B82F6'
  },
  dorian: {
    id: 'dorian',
    name: 'Dorian ORB',
    shortDesc: 'Direct, resilient, and action‑oriented',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/bi6kcptf_Dorian%20Standard.png',
    color: '#F59E0B'
  },
  octavio: {
    id: 'octavio',
    name: 'Octavio ORB',
    shortDesc: 'Creative, expressive, and energizing',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/zi3ibx7q_Octavio%20Standard.png',
    color: '#10B981'
  }
};

// Hoisted to module scope so OrbCharactersPage's re-renders never
// remount the <img> tags (which used to cause a visible flicker).
const CharacterCard = ({ character, onClick }) => {
  if (!character.image) {
    return (
      <button
        onClick={onClick}
        className="flex flex-col items-center p-4 rounded-2xl bg-white/80 dark:bg-[#1a1a1a]/80 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all hover:scale-105"
        data-testid={`character-card-${character.id}`}
      >
        <div
          className="w-20 h-20 rounded-xl flex items-center justify-center mb-3"
          style={{ backgroundColor: `${character.color}20` }}
        >
          <Sparkles className="w-10 h-10" style={{ color: character.color }} />
        </div>
        <h3 className="font-semibold text-sm text-center" style={{ color: character.color }}>
          {character.name}
        </h3>
        <p className="text-[10px] text-muted-foreground text-center mt-1">
          {character.shortDesc}
        </p>
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center p-4 rounded-2xl bg-white/80 dark:bg-[#1a1a1a]/80 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all hover:scale-105"
      data-testid={`character-card-${character.id}`}
    >
      <div
        className="w-20 h-28 rounded-xl overflow-hidden border-2 mb-3"
        style={{ borderColor: character.color }}
      >
        <img
          src={character.image}
          alt={character.name}
          className="w-full h-full object-cover object-top"
        />
      </div>
      <h3 className="font-semibold text-sm text-center" style={{ color: character.color }}>
        {character.name}
      </h3>
      <p className="text-[10px] text-muted-foreground text-center mt-1">
        {character.shortDesc}
      </p>
    </button>
  );
};

const OrbCharactersPage = () => {
  const navigate = useNavigate();
  const { user, isFeatureEnabled } = useAuth();
  usePageHeader({ title: 'Characters' }, []);
  
  // Check if user needs onboarding
  const needsOnboarding = user?.role !== 'admin' && !user?.is_onboarded;

  // Map character IDs to their feature toggle IDs
  const characterFeatureMap = {
    orb: 'orb_ai_assistant',
    mena: 'orb_mena_entire',
    katie: 'orb_katie_entire',
    luke: 'orb_luke_entire',
    dorian: 'orb_dorian_entire',
    octavio: 'orb_octavio_entire'
  };

  // Filter characters based on feature toggles
  const visibleCharacters = Object.values(AI_CHARACTERS).filter(character => {
    const featureId = characterFeatureMap[character.id];
    return !featureId || isFeatureEnabled(featureId);
  });

  // Show locked view for non-onboarded users
  if (needsOnboarding) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] flex items-center justify-center p-4" data-testid="orb-characters-locked">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-10 h-10 text-gray-400" />
          </div>
          <h1 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">ORB Characters Locked</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Complete your onboarding process to meet the ORB Characters and access their exclusive content, music, and chat features.
          </p>
          <Button 
            onClick={() => navigate('/')}
            className="bg-[#586D52] hover:bg-[#4a5d46]"
          >
            Continue Onboarding
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24" data-testid="orb-characters-page">
      {/* iter159 — the standalone header description previously above
          the "How We Use AI Characters" box is now consolidated INSIDE
          the same box, so there's a single explanation panel. */}
      <div className="bg-white/90 dark:bg-[#1a1a1a]/90 rounded-2xl p-4 mb-6 shadow-sm border border-gray-100 dark:border-gray-800">
        <h2 className="font-semibold text-base mb-2 text-[#586D52] dark:text-[#8B9D83]">
          How We Use AI Characters
        </h2>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Each ORB character brings a unique perspective and personality to guide you through The Uplift.
          Whether you need strategic advice, emotional support, creative inspiration, or straightforward
          guidance, there's an ORB character designed to meet you where you are. Our AI characters are
          more than just chat assistants&mdash;they're designed to provide personalized support tailored
          to your needs and communication style. Each character has been crafted with distinct traits
          and expertise to help you navigate your journey with The Uplift. Choose the character that
          resonates with you, or switch between them depending on what kind of support you need.
        </p>
      </div>

      {/* Characters Grid */}
      <h2 className="font-semibold text-lg mb-4 text-center">Choose Your Guide</h2>
      <div className="grid grid-cols-2 gap-3">
        {visibleCharacters.map((character) => (
          <CharacterCard
            key={character.id}
            character={character}
            onClick={() => navigate(`/character/${character.id}`)}
          />
        ))}
      </div>
    </div>
  );
};

export default OrbCharactersPage;
